import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import {
    AppointmentWizardSettingsStore,
    familyWizardFactory,
    professionalWizardFactory,
} from './appointment-wizard-settings.store';
import { Observable } from 'rxjs';
import { LanguageService } from '../../../core/services/language/language.service';
import { SettingsQuery } from '../settings/settings.query';
import { UserQuery } from '../user/user.query';
import { ClinicDetailQuery } from '../clinic-detail/clinic-detail.query';
import { FamilyWizardModel } from '../../models/family-wizard.model';
import { ServiceWizardService } from '../service-wizard/service-wizard.service';
import { AppointmentWizardSettingsQuery } from './appointment-wizard-settings.query';
import { AppointmentWizardTime } from '../appointment-wizard-time/appointment-wizard-time.model';
import { ServiceWizardQuery } from '../service-wizard/service-wizard.query';
import { AppointmentWizardDateQuery } from '../appointment-wizard-date/appointment-wizard-date.query.';
import { AppointmentWizardTimeQuery } from '../appointment-wizard-time/appointment-wizard-time.query';
import { ApiService } from 'src/app/core/services/api/api.service';
import { ProfessionalWizard } from '../../models/professional-wizard.model';
import { ServicesGroupQuery } from '../services-group/services-group.query';
import { ClinicWizardModel } from '../../models/clinic-wizard.model';
import { PacksWizardService } from '../pack-wizard/pack-wizard.service';

@Injectable({ providedIn: 'root' })
export class AppointmentWizardSettingsService {
    constructor(
        private appointmentWizardSettingsStore: AppointmentWizardSettingsStore,
        private languageService: LanguageService,
        private settingsQuery: SettingsQuery,
        private userQuery: UserQuery,
        private clinicDetailQuery: ClinicDetailQuery,
        private serviceWizardService: ServiceWizardService,
        private appointmentWizardSettingsQuery: AppointmentWizardSettingsQuery,
        private serviceWizardQuery: ServiceWizardQuery,
        private appointmentWizardDateQuery: AppointmentWizardDateQuery,
        private appointmentWizardTimeQuery: AppointmentWizardTimeQuery,
        private apiService: ApiService,
        private servicesGroupQuery: ServicesGroupQuery,
        private packsWizardService: PacksWizardService
    ) {}

    /*-----------*/
    /** UPDATES */
    /*----------*/
    updateFamilies(newFamilies: FamilyWizardModel[]) {
        this.appointmentWizardSettingsStore.update({ families: newFamilies });
    }

    updateClinics(newClinics: ClinicWizardModel[]) {
        this.appointmentWizardSettingsStore.update({ clinics: newClinics });
    }

    updateProfessionals(newProfessionals: ProfessionalWizard[]) {
        this.appointmentWizardSettingsStore.update({
            professionals: newProfessionals,
        });
    }

    /** TODO: He intentado usar la función de akita arrayAdd pero no me funciona, uso este método para añadir elementos a un array */
    pushStepStack(step: number) {
        const currentStepStack = this.appointmentWizardSettingsQuery.getValue()
            ?.stepStack;
        const cloneStepStack = [...currentStepStack];
        cloneStepStack.push(step);
        this.appointmentWizardSettingsStore.update({
            stepStack: cloneStepStack,
        });
    }

    /** TODO: He intentado usar la función de akita arrayAdd pero no me funciona, uso este método para añadir elementos a un array */
    popStepStack() {
        const currentStepStack = this.appointmentWizardSettingsQuery.getValue()
            ?.stepStack;
        const cloneStepStack = [...currentStepStack];
        cloneStepStack.pop();
        this.appointmentWizardSettingsStore.update({
            stepStack: cloneStepStack,
        });
    }

    /** TODO: He intentado usar la función de akita arrayAdd pero no me funciona, uso este método para añadir elementos a un array */
    removeStepFromStepStack(step: number) {
        const currentStepStack = this.appointmentWizardSettingsQuery.getValue()
            ?.stepStack;
        const cloneStepStack = [...currentStepStack].filter(
            (item) => item !== step
        );
        this.appointmentWizardSettingsStore.update({
            stepStack: cloneStepStack,
        });
    }

    clearStepStack() {
        this.appointmentWizardSettingsStore.update({
            stepStack: [],
        });
    }

    /*---------------*/
    /** LLAMADAS API */
    /*---------------*/

    /**
     * Llamada api para obtener la configuración del asistente de pedir cita
     */
    private getAppointmentWizardSettingsApi(
        systemKey: string,
        tokenId: string,
        clinicId: string
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClientToken', tokenId);
        params = params.append('ClinicID', clinicId);
        params = params.append('Cmd', 'c1041');
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /**
     * Llamada api para obtener la configuración del asistente de pedir cita
     */
    private makeAppointmentSinlgeGroupApi(
        systemKey: string,
        clientToken: string,
        clinicId: string,
        services: string[],
        professional: ProfessionalWizard,
        time: AppointmentWizardTime
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClientToken', clientToken);
        params = params.append('ClinicID', clinicId);
        params = params.append('Services', services.join(','));
        params = params.append(
            'ProfessionalID',
            professional ? professional.id : ''
        );
        params = params.append(
            'ProfessionalName',
            professional ? professional.description : ''
        );
        params = params.append('DiaryDate', time.date);
        params = params.append('TimeStart', time.timeStart);
        params = params.append('TimeEnd', time.timeEnd);
        params = params.append('CabineID', time.cabinetId);
        params = params.append('Cmd', 'c1043');
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /**
     * Llamada api para obtener la configuración del asistente de pedir cita
     */
    private makeAppointmentMultipleGroupApi(
        systemKey: string,
        clientToken: string,
        servicesGroup: any
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClientToken', clientToken);
        params = params.append('CompositeApp', servicesGroup);
        params = params.append('Cmd', 'c1047');
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /*------------*/
    /** SERVICIOS */
    /*------------*/
    /** Este servicio llama a la API para recoger los datos y la configuración inicial del asistente de pedir cita y rellena el store */
    async getAppointmentWizardSettings() {
        const appointmentWizardSettingsData = await this.getAppointmentWizardSettingsApi(
            this.settingsQuery.systemKeyURL,
            this.userQuery.tokenId,
            this.clinicDetailQuery.id
        ).toPromise();
        this.appointmentWizardSettingsStore.update({
            servicesMultiSelect:
                appointmentWizardSettingsData.ServicesMultiSelect != null
                    ? appointmentWizardSettingsData.ServicesMultiSelect === '-1'
                    : false,
            professionalsEnabled:
                appointmentWizardSettingsData.ServiceProfessionalsEnabled !=
                null
                    ? appointmentWizardSettingsData.ServiceProfessionalsEnabled ===
                      '-1'
                    : false,
        });
        this.serviceWizardService.setServicesWizardByApi(
            appointmentWizardSettingsData.Services
        );
        this.setFamiliesWizardByApi(appointmentWizardSettingsData.Families);
        this.setProfessionalsByApi(appointmentWizardSettingsData.Professionals);
        this.packsWizardService.setPacksByApi(appointmentWizardSettingsData?.PackList);
    }

    /**
     * Crea las familias a partir de los datos de la API y los carga en el Store
     */
    setFamiliesWizardByApi(families) {
        if (families?.length > 0) {
            const familiesWizardEntities = [];
            families.forEach((familyWizard) => {
                familiesWizardEntities.push(familyWizardFactory(familyWizard));
            });

            this.updateFamilies(familiesWizardEntities);
        }
    }

    setProfessionalsByApi(professionals) {
        if (professionals?.length > 0) {
            const professionalsEntities = [];
            professionals.forEach((professional) => {
                professionalsEntities.push(
                    professionalWizardFactory(professional)
                );
            });
            this.updateProfessionals(professionalsEntities);
        }
    }

    async makeAppointmentSingleGroup(currentClinicId: string): Promise<string> {
        /** Los servicios seleccionados los cogemos de Akita */
        const servicesChekedArrayId = [];
        this.serviceWizardQuery
            .getAll({
                filterBy: ({ checked }) => checked === true,
            })
            .forEach((service) => {
                servicesChekedArrayId.push(service.id);
            });
        const professionalChecked: ProfessionalWizard = this.servicesGroupQuery.getAll()[0]
            .professionalSelected;
        const timeActive = this.appointmentWizardTimeQuery.getActive();

        const appointmentData = await this.makeAppointmentSinlgeGroupApi(
            this.settingsQuery.systemKeyURL,
            this.userQuery.tokenId,
            currentClinicId,
            servicesChekedArrayId,
            professionalChecked,
            timeActive
        ).toPromise();

        return appointmentData.AppID;
    }

    async makeAppointmentMultipleGroup(): Promise<string> {
        const timeActive = this.appointmentWizardTimeQuery.getActive();

        const JsonTimeActive = {
            CompositeApp: {
                ClinicID: timeActive.clinicId,
                Date: timeActive.date,
                TimeStart: timeActive.timeStart,
                TimeEnd: timeActive.timeEnd,
                TotalWait: timeActive.totalWait,
                ServicesGroups: timeActive.servicesGroup,
            },
        };
        const appointmentData = await this.makeAppointmentMultipleGroupApi(
            this.settingsQuery.systemKeyURL,
            this.userQuery.tokenId,
            JSON.stringify(JsonTimeActive)
        ).toPromise();

        return appointmentData.AppID;
    }

    resetStore() {
        this.appointmentWizardSettingsStore.reset();
    }
}
